/**
 * Search in the catalogue that forgives what happens while typing: typos ("tomatn"), missing or
 * spelled-out umlauts ("apfel", "muesli"), singular or plural ("tomaten" finds "Tomate"), words in
 * any order ("tomaten passiert"). Every word of the query has to fit somewhere in a name; a closer
 * fit ranks higher, the usual name a little higher than a synonym. Taken over from Lagerlog's wine
 * search (packages/core/src/search.ts there), with the shared normalization and stems.
 */
import { PRODUCTS, aisle } from "./catalog.ts";
import { lookup } from "./lookup.ts";
import type { AisleKind, Product } from "./catalog.ts";
import { fold, foldSpelledOut, nameKey, stem } from "./normalize.ts";

/** Edit distance with a ceiling: stops early once it is above `max`. Two swapped letters are one mistake. */
export function distance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  let before: number[] = [];
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      let d = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d = Math.min(d, before[j - 2]! + 1);
      cur[j] = d;
      best = Math.min(best, d);
    }
    if (best > max) return max + 1;
    before = prev;
    prev = cur;
  }
  return prev[b.length]!;
}

/** How many mistakes a word may have: none for short ones and numbers, so "ei" and "1,5" stay exact. */
const allowed = (word: string) => (/\d/.test(word) || word.length <= 3 ? 0 : word.length <= 6 ? 1 : 2);

/** How well one folded query word fits a name's words: 0 best, null not at all. */
function wordScore(word: string, words: readonly string[], text: string): number | null {
  if (words.includes(word)) return 0;
  const stemmed = stem(word);
  if (words.some((w) => stem(w) === stemmed)) return 0.5;
  if (words.some((w) => w.startsWith(word))) return 1;
  if (text.includes(word)) return 2;
  const max = allowed(word);
  if (max === 0) return null;
  let best: number | null = null;
  for (const w of words) {
    // A typed word may be the beginning of a longer one ("kichere" for "kichererbsen").
    const candidates = w.length > word.length ? [w, w.slice(0, word.length)] : [w];
    for (const c of candidates) {
      const d = distance(word, c, max);
      if (d <= max) best = Math.min(best ?? Infinity, 2 + d);
    }
  }
  return best;
}

/** The score of folded query words against a folded text, lower is better; null when a word does not fit. */
function score(query: readonly string[], text: string): number | null {
  const words = text.split(" ");
  let total = 0;
  for (const word of query) {
    const s = wordScore(word, words, text);
    if (s === null) return null;
    total += s;
  }
  return total;
}

/**
 * A score for `text` against `query`, lower is better, or null when a word does not fit at all.
 * An empty query fits everything with 0. "ae", "oe", "ue" in the query may stand for an umlaut.
 */
export function searchScore(text: string, query: string): number | null {
  const folded = fold(text);
  const forms = [...new Set([fold(query), foldSpelledOut(query)])].map((q) => q.split(" ").filter(Boolean));
  let best: number | null = null;
  for (const words of forms) {
    if (words.length === 0) return 0;
    const s = score(words, folded);
    if (s !== null && (best === null || s < best)) best = s;
  }
  return best;
}

export interface SearchOptions {
  /** At most this many hits (8). */
  limit?: number;
  /** Only products of aisles of these kinds: ["food"] for VorratsBuch, ["food", "household"]. */
  kinds?: readonly AisleKind[];
  /** Search the English names too (the translation mode of the EinkaufsZettel). */
  english?: boolean;
}

export interface Hit {
  product: Product;
  /** The name that fit best: a synonym when the query was one ("Passata"). */
  name: string;
  score: number;
}

const FOLDED = PRODUCTS.map((p) => ({ p, names: p.names.map(fold), keys: p.names.map(nameKey), en: (p.en ?? []).map(fold) }));

/** A query that is a whole name ("Tomaten" for "Tomate") beats one that is only a word of it ("Passierte Tomaten"). */
const WHOLE = -1;
/**
 * What lookup() makes of the whole query ("Himbeereis" is ice cream, "Tomaten (Dose)" a tin of
 * tomatoes) ranks after a beginning of a name and before any typo.
 */
const MEANT = 1.5;

/** Products that fit a query, best first, each with the name that fit. */
export function search(query: string, opts: SearchOptions = {}): Hit[] {
  const forms = [...new Set([fold(query), foldSpelledOut(query)])].map((q) => q.split(" ").filter(Boolean));
  if (forms[0]!.length === 0) return [];
  const whole = new Set(forms.map((words) => words.map(stem).join(" ")));
  const hits: Hit[] = [];
  for (const { p, names, keys, en } of FOLDED) {
    if (opts.kinds && !opts.kinds.includes(aisle(p.aisle)?.kind ?? "food")) continue;
    // The usual name first, synonyms a little lower, English lower still.
    const candidates = names.map((folded, i) => ({ folded, name: p.names[i]!, extra: (i ? 0.5 : 0) + (whole.has(keys[i]!) ? WHOLE : 0) }));
    if (opts.english) candidates.push(...en.map((folded, i) => ({ folded, name: p.en![i]!, extra: 1 })));
    let best: Hit | undefined;
    for (const { folded, name, extra } of candidates) {
      for (const words of forms) {
        const s = score(words, folded);
        if (s !== null && (best === undefined || s + extra < best.score)) best = { product: p, name, score: s + extra };
      }
    }
    if (best) hits.push(best);
  }
  const meant = lookup(query)?.product;
  if (meant && (!opts.kinds || opts.kinds.includes(aisle(meant.aisle)?.kind ?? "food"))) {
    const known = hits.find((h) => h.product === meant);
    if (!known) hits.push({ product: meant, name: meant.names[0]!, score: MEANT });
    else if (known.score > MEANT) known.score = MEANT;
  }
  return hits
    .sort((a, b) => a.score - b.score || a.product.names[0]!.length - b.product.names[0]!.length || a.product.names[0]!.localeCompare(b.product.names[0]!, "de"))
    .slice(0, opts.limit ?? 8);
}

/** The products of search(), without the names that fit. */
export const searchProducts = (query: string, opts: SearchOptions = {}): Product[] => search(query, opts).map((h) => h.product);
